import { Button, ButtonProps, Empty, Row, Select, SelectProps, Skeleton, Typography, theme } from 'antd';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GrispiInputProps } from '../..';
import useAutocompleteSuggestions from '../../../../hooks/autocomplete/useAutocompleteSuggestions';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import useAutocompleteSearch from '../../../../hooks/autocomplete/useAutocompleteSearch';
import useSingleAutocompleteItemById from '../../../../hooks/autocomplete/useSingleAutoCompleteItemById';
import useDebounce from '../../../../hooks/useDebounce';
import useToggle from '../../../../hooks/useToggle';
import { getUserIdFromUrl } from '../../../../store/childStore/tabStore';
import { nullishValue } from '../../../../utils/helpers';
import CustomDivider from '../../../CustomDivider';
import LoadingMenu from '../../../LoadingMenu';
import GrispiInputContainer from '../../GrispiInputContainer';

export enum AutoCompleteKeyMap {
    organization = 'organization',
    group = 'group',
    form = 'form',
    filter = 'filter',
    endUserForm = 'end-user-form',
    advancedReport = 'advanced-report',
    automation = 'automation',
    userFilter = 'user-filter',
    trigger = 'trigger'
}
export enum AutoCompleteEndpointMap {
    organization = 'organization',
    group = 'group',
    form = 'ts.form',
    filter = 'filter',
    'end-user-form' = 'end-user-form',
    'advanced-report' = 'advanced-report',
    'automation' = 'automation',
    'user-filter' = 'user-filter',
    'trigger' = 'trigger'
}
export enum AutoCompleteSingleItemEndpointMap {
    organization = '/organizations',
    group = '/groups',
    form = '/forms',
    filter = '/field-values/filter',
    'end-user-form' = '/user-forms',
    'advanced-report' = '/advanced-reports',
    'automation' = '/automations',
    'user-filter' = '/user-filters',
    'trigger' = '/triggers'
}
export const autoCompleteItemNameListCreatedBySystem = [
    'Default Group',
    'Agents',
    'Chat',
    'Call',
    'Default form',
    'Your Unsolved Tickets',
    'Unassigned Tickets',
    'Solved Tickets',
    'Recently Updated Tickets',
    'Pending Tickets',
    'All Unsolved Tickets',
    'Agent Reply Pending',
    'First Contact Pending'
];

type props = Omit<GrispiInputProps<'AUTOCOMPLETE'>, 'type'>;

const initialSearchTerm = '';

const DynamicOrganizationModal = dynamic(() => import('../../../OrganizationDetailPage/OrganizationCreateModal/index'));

/* The `GrispiAutoCompleteInput` component is a custom input component that provides autocomplete
functionality. It is working for `ORGANIZATION`, `GROUP` and `FORM` */
const GrispiAutoCompleteInput = ({
    isNewValueAllowed = false,
    autocompleteKey = AutoCompleteKeyMap.organization,
    onBlur,
    onChange,
    options = [],
    value,
    antdProps = {},
    containerProps,
    ...rest
}: props) => {
    const { t } = useTranslation('forms');
    const {
        token: { marginXXS, colorTextLightSolid }
    } = theme.useToken();
    const { asPath } = useRouter();

    const addButtonRef = useRef<HTMLSelectElement>();
    const isNewValueAllowedForOrganization = isNewValueAllowed && autocompleteKey === AutoCompleteKeyMap.organization;
    const [isOrganizationModalOpen, , openOrganizationModal, closeOrganizationModal] = useToggle(false);
    const [modalValue, setModalValue] = useState('');

    const isPublic = autocompleteKey === AutoCompleteKeyMap.endUserForm;
    const { data: singleItem, isLoading: isSingleItemLoading } = useSingleAutocompleteItemById(
        autocompleteKey,
        value,
        isPublic
    );
    const [searchTerm, setSearchTerm] = useState<string>(initialSearchTerm);
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const { data: searchResult, isLoading } = useAutocompleteSearch(autocompleteKey, debouncedSearchTerm, isPublic);
    const {
        data: suggestions,
        setIsSuggestionRequested,
        isLoading: isLoadingSuggestions
    } = useAutocompleteSuggestions(autocompleteKey, !searchResult, isPublic);
    const suggestionsWithoutSingleItem = suggestions?.filter((item) => item.value !== singleItem?.id);

    const groupOptions = useMemo(
        () =>
            options.concat(
                singleItem
                    ? [
                          {
                              label: autoCompleteItemNameListCreatedBySystem.includes(singleItem?.name)
                                  ? t(`form.autoCompleteItems.${singleItem?.name}` as any)
                                  : singleItem?.name,
                              value: singleItem?.id
                          }
                      ]
                    : []
            ),
        [singleItem, options, t]
    );
    const handleOnClick: ButtonProps['onClick'] = () => {
        setModalValue(searchTerm);
        openOrganizationModal();
    };
    const onEnterKeyDown: SelectProps<string | string[]>['onInputKeyDown'] = ({ key }) => {
        if (key === 'Enter' && addButtonRef.current) addButtonRef.current.click();
    };

    const handleOnChange: SelectProps<number>['onChange'] = (value) => {
        if (nullishValue(value)) {
            onChange(null);
            return;
        }
        onChange(value);
        setSearchTerm(initialSearchTerm);
    };
    const handleOnBlur: SelectProps<number>['onBlur'] = () => {
        if (onBlur) onBlur();
        setSearchTerm(initialSearchTerm);
    };
    const realOptions = useMemo(() => {
        return isLoading
            ? []
            : searchResult
            ? [...searchResult]
            : suggestionsWithoutSingleItem
            ? [...groupOptions, ...suggestionsWithoutSingleItem]
            : groupOptions;
    }, [isLoading, searchResult, suggestionsWithoutSingleItem, groupOptions]);

    const isLoadingContent = isLoading || isSingleItemLoading || isLoadingSuggestions;

    const notFoundContent = isLoadingContent ? (
        <LoadingMenu />
    ) : debouncedSearchTerm === initialSearchTerm ? (
        <Row justify="center">
            <Typography.Text style={{ marginBottom: marginXXS }}> {t('autocomplete.empty')}</Typography.Text>
        </Row>
    ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}>
            {isNewValueAllowedForOrganization ? (
                <Button type="primary" onClick={handleOnClick} ref={addButtonRef} className="full-width">
                    <Typography.Text style={{ color: colorTextLightSolid }} ellipsis>
                        {`${t('autocomplete.organizationAdd')} "${searchTerm || debouncedSearchTerm}"`}
                    </Typography.Text>
                </Button>
            ) : null}
        </Empty>
    );
    const isSuggestionTextRender = !debouncedSearchTerm && !searchResult?.length && !!realOptions?.length;
    const isNoResultTextRender =
        !!debouncedSearchTerm && Array.isArray(searchResult) && !searchResult?.length && !isLoading;
    const isSuggestionLoadingRender = !isLoading && !!groupOptions?.length && isLoadingSuggestions;
    if (isSingleItemLoading)
        return (
            <GrispiInputContainer {...containerProps}>
                <Skeleton.Input active className="full-width" />
            </GrispiInputContainer>
        );
    return (
        <>
            <GrispiInputContainer {...containerProps}>
                <Select<number>
                    allowClear
                    showSearch
                    loading={isSingleItemLoading || isLoading}
                    onChange={handleOnChange}
                    onBlur={handleOnBlur}
                    onInputKeyDown={onEnterKeyDown}
                    onSearch={(value) => setSearchTerm(value)}
                    optionLabelProp="label"
                    notFoundContent={notFoundContent}
                    autoClearSearchValue
                    options={realOptions}
                    getPopupContainer={(trigger) => trigger.parentElement}
                    dropdownRender={(option) => (
                        <>
                            {isSuggestionTextRender && (
                                <Row justify="center">
                                    <Typography.Text style={{ marginBottom: marginXXS }}>
                                        {t('autocomplete.empty')}
                                    </Typography.Text>
                                    <CustomDivider zeroMargin />
                                </Row>
                            )}
                            {isNoResultTextRender && (
                                <Row justify="center">
                                    <Typography.Text style={{ marginBottom: marginXXS }}>
                                        {t('autocomplete.noResults')}
                                    </Typography.Text>
                                    <CustomDivider zeroMargin />
                                </Row>
                            )}
                            {option}
                            {isSuggestionLoadingRender && <LoadingMenu />}
                        </>
                    )}
                    filterOption={false}
                    value={!+value ? value : +value}
                    onDropdownVisibleChange={(visible) => {
                        setSearchTerm(initialSearchTerm);
                        setIsSuggestionRequested(visible);
                    }}
                    {...antdProps}
                    {...rest}
                />
            </GrispiInputContainer>
            {isNewValueAllowed && isOrganizationModalOpen && (
                <DynamicOrganizationModal
                    isOpen={isOrganizationModalOpen}
                    userId={getUserIdFromUrl(asPath)}
                    closeOrganizationModal={closeOrganizationModal}
                    modalValue={modalValue}
                    setModalValue={(orgId: number) => {
                        onChange(orgId);
                    }}
                />
            )}
        </>
    );
};

export default GrispiAutoCompleteInput;
